import React, { useState } from 'react';
import { ChevronRight, GitBranch, Sparkles } from 'lucide-react';
import { TaglinePhilosophyModal } from '../../components/common/TaglinePhilosophyModal';

export const HistoryPage: React.FC = () => {
  const [isPhilosophyModalOpen, setIsPhilosophyModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<number>(1);

  const historyMilestones = [
    {
      step: 1,
      year: 'Latar Belakang & Pengasas',
      title: 'Perguruan Awal & Pawang Diraja Perak',
      location: 'Patani & Kg. Manjoi, Ipoh',
      description: 'Perguruan ini diasaskan oleh Allahyarham Tuan Haji Shafie bin Zahid, seorang guru silat disegani yang pernah memegang amanah sebagai Pawang Diraja Sultan Perak. Beliau menuntut ilmu persilatan di Patani, Thailand (pusat perkembangan silat Melayu) sebelum mendirikan gelanggang awal di Kampung Manjoi, Ipoh, Perak.'
    },
    {
      step: 2,
      year: 'Tahun 1969',
      title: 'Pengijazahan & Pewarisan Ilmu',
      location: 'Ipoh, Perak',
      description: 'Pada tahun 1969, Allahyarham Tuan Haji Shafie mengijazahkan keseluruhan silibus ilmu persilatan kepada Tuan Guru Azmi bin Abd Hamid, Encik Azli Abd Hamid, dan anak kandung Allahyarham iaitu Encik Azman bin Shafie.'
    },
    {
      step: 3,
      year: 'Tahun 1983',
      title: 'Perkembangan Induk di Kedah',
      location: 'Simpang Tiga Keladi, Kulim',
      description: 'Tuan Guru Azmi bin Abd Hamid berpindah ke Kulim, Kedah pada tahun 1983 dan mendirikan gelanggang utama di Kampung Simpang Tiga Keladi. Gelanggang ini menjadi tapak semaian dan pusat perkembangan utama Silat Awang Sulung Merah Muda di Kedah Darul Aman.'
    },
    {
      step: 4,
      year: '2007 / 2022',
      title: 'Pendaftaran Rasmi Pertubuhan (ROS)',
      location: 'Jabatan Pendaftaran Pertubuhan Malaysia',
      description: 'Didaftarkan secara legasi pada tahun 2007 (No. 2589/2007) dan kini beroperasi di bawah pendaftaran rasmi Jabatan Pendaftaran Pertubuhan Malaysia (ROS) Kedah dengan No. Pendaftaran PPM-007-02-28082022.'
    },
    {
      step: 5,
      year: 'Gelanggang Warisan',
      title: 'Peluasan Gelanggang & Jurulatih',
      location: 'Kulim, Lunas & Paya Besar',
      description: 'Perkembangan ilmu diteruskan melalui barisan kepimpinan jurulatih perguruan serta pengukuhan gelanggang latihan masa kini seperti Pusaka Warisan (Lunas), Wadi AnNour, dan Paya Besar.'
    }
  ];

  return (
    <div className="w-full space-y-8 min-h-[75vh] bg-white text-slate-900 pb-16">
      {/* Main History Overview Card with Overlapping Logo Anchor */}
      <div className="relative pt-10 sm:pt-12 mb-4">
        {/* Overlapping Focal Emblem Anchor */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-md bg-white p-2.5 flex items-center justify-center">
            <img
              src="/logo-pssasmm.png"
              alt="Logo PSSASMM"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10 pt-14 sm:pt-16 space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 font-extrabold bg-amber-50 border border-amber-200 px-3 py-1 rounded-full uppercase tracking-wider">
              <span>Salasilah & Asal-Usul Pusaka</span>
            </span>
            <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Asal-Usul & Latar Belakang Perguruan ASMM
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
              Persatuan Seni Silat Awang Sulung Merah Muda Kedah Darul Aman didaftarkan secara rasmi di bawah Jabatan Pendaftaran Pertubuhan Malaysia (ROS) dengan No. Pendaftaran <strong>PPM-007-02-28082022</strong> (Legacy No: 2589/2007). Perguruan ini membawa aliran silat Melayu asli yang menekankan ketangkasan fizikal, pembentukan sahsiah mulia, dan pertahanan diri yang realistik.
            </p>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-medium">
              Diasaskan oleh <strong>Allahyarham Tuan Haji Shafie bin Zahid</strong> (bekas Pawang Diraja Sultan Perak), seni persilatan ini kemudiannya diijazahkan kepada <strong>Tuan Guru Azmi bin Abd Hamid</strong> pada tahun 1969 sebelum diperkembangkan di Kedah bermula di Kampung Simpang Tiga Keladi, Kulim pada tahun 1983.
            </p>
          </div>

          {/* Tagline Philosophy Banner Callout Card */}
          <div className="p-5 sm:p-6 rounded-2xl bg-slate-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-slate-800 shadow-md">
            <div className="space-y-1">
              <span className="text-[10px] font-extrabold text-amber-400 uppercase tracking-widest block">
                Falsafah Tertinggi Perguruan
              </span>
              <h4 className="text-base sm:text-lg font-extrabold text-white">"Biduk Berlalu, Kiambang Bertaut"</h4>
              <p className="text-xs text-slate-300">Terokai kisah Ujian Pendekar Muda Awang Sulung & falsafah penyatuan ukhuwah.</p>
            </div>

            <button
              onClick={() => setIsPhilosophyModalOpen(true)}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs rounded-full transition-all shadow-sm flex items-center gap-2 shrink-0 cursor-pointer"
            >
              <span>Baca Kisah Falsafah</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Chronological Flow Section (Aliran Sejarah Awal) */}
          <div className="pt-6 border-t border-slate-100 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-red-600" />
                <h4 className="font-extrabold text-base sm:text-lg text-slate-900">Aliran & Susur Galur Sejarah Awal</h4>
              </div>
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider hidden sm:block">
                Perjalanan Warisan ASMM
              </span>
            </div>

          {/* Interactive Timeline Flow Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-stretch">
            {historyMilestones.map((m) => {
              const isSelected = activeStep === m.step;

              return (
                <button
                  key={m.step}
                  onClick={() => setActiveStep(m.step)}
                  className={`p-4 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between h-full min-h-[135px] space-y-2 ${isSelected
                      ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/20'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                    }`}
                >
                  {/* Card Header Year Tag (Fixed height for horizontal alignment) */}
                  <div className="h-6 flex items-center justify-between w-full shrink-0">
                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md truncate max-w-full ${isSelected ? 'bg-slate-800 text-amber-400' : 'bg-white text-slate-600 border border-slate-200'
                      }`}>
                      {m.year}
                    </span>
                  </div>

                  {/* Card Title & Location (Fixed min-height for title alignment) */}
                  <div className="flex-1 flex flex-col justify-between pt-1">
                    <h5 className={`font-extrabold text-xs leading-snug min-h-[2.5rem] flex items-start ${isSelected ? 'text-white' : 'text-slate-900'
                      }`}>
                      {m.title}
                    </h5>
                    <p className={`text-[10px] font-semibold truncate pt-1 ${isSelected ? 'text-slate-300' : 'text-slate-500'
                      }`}>
                      📍 {m.location}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected Milestone Detail Banner */}
          {activeStep && (
            <div className="p-5 bg-amber-50/80 border border-amber-200 rounded-2xl space-y-2 animate-fade-in">
              <div className="flex items-center gap-2 text-amber-900 font-extrabold text-xs uppercase tracking-wider">

                <span>Lanjutan Sejarah Awal — {historyMilestones[activeStep - 1].year} ({historyMilestones[activeStep - 1].location})</span>
              </div>
              <h5 className="font-extrabold text-sm text-slate-900">
                {historyMilestones[activeStep - 1].title}
              </h5>
              <p className="text-xs text-slate-700 leading-relaxed font-medium">
                {historyMilestones[activeStep - 1].description}
              </p>
            </div>
          )}
        </div>

        {/* 3 Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-100">
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
            <div className="font-bold text-slate-900 text-sm">
              <span>Teras Pertahanan Diri</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Mempelajari teknik elakkan, tangkisan tapak, kuncian sendi, dan serangan balas pantas yang berkesan dalam situasi kecemasan sebenar.
            </p>
          </div>

          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
            <div className="font-bold text-slate-900 text-sm">
              <span>Adab & Sahsiah</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Memupuk taat setia kepada agama, ibu bapa, perguruan, dan tanah air mengikut perlembagaan persatuan berlandaskan undang-undang ROS.
            </p>
          </div>

          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
            <div className="font-bold text-slate-900 text-sm">
              <span>Silibus Rasmi ROS</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Sukatan pengajian berperingkat bermula dari Bengkung Hitam, Putih, Merah, sehingga Kuning mengikut ketetapan latihan rasmi pertubuhan.
            </p>
          </div>
        </div>
      </div>
      </div>

      {/* Tagline Philosophy Modal */}
      <TaglinePhilosophyModal
        isOpen={isPhilosophyModalOpen}
        onClose={() => setIsPhilosophyModalOpen(false)}
      />
    </div>
  );
};


